import { devLog, shortenAddress } from '@/utils';
import { TokenHistory, TokenHistoryItem, TopItem, ProjectDetail, CexDetail } from './types';
import jsonexport from 'jsonexport';
import { downloadCSV } from './components/AnalysisModeButton/utils';
import message from 'antd/lib/message';
import { getExplorerLink, getTokenSymbol, formatNum } from '@/utils';
import dayjs from 'dayjs';

const TOP_CATEGORY_COUNT = 20;
const TOP_TOKEN_COUNT = 20;

const addNewRecordValueToMap = (map: Record<string, number>, key: string, value: number) => {
  if (!map[key]) {
    map[key] = 0;
  }
  map[key] += value;
};

const convertProjectValueMapToTopList = (
  valueMap: Record<string, number>,
  project_dict: Record<string, ProjectDetail>,
) => {
  return Object.keys(valueMap).map(projectId => {
    const project = project_dict[projectId];

    return {
      value: valueMap[projectId],
      type: 'project' as const,
      chain: project.chain,
      name: project.name,
      icon: project.logo_url,
      id: projectId,
    };
  });
};

const convertCexValueMapToTopList = (
  valueMap: Record<string, number>,
  cex_dict: Record<string, CexDetail>,
) => {
  return Object.keys(valueMap).map(cexId => ({
    value: valueMap[cexId],
    type: 'cex' as const,
    name: cex_dict[cexId].name,
    icon: cex_dict[cexId].logo_url,
    id: cexId,
    isDeposit: cex_dict[cexId]?.is_deposit,
  }));
};

const convertAddrValueMapToTopList = (valueMap: Record<string, number>) => {
  return Object.keys(valueMap).map(addr => ({
    value: valueMap[addr],
    type: 'addr' as const,
    name: shortenAddress(addr),
    id: addr,
  }));
};

export const analysisHistoryList = (
  historyData: TokenHistory,
  orConditionFnList: ((record: TokenHistoryItem) => boolean)[] = [],
  andConditionList: ((record: TokenHistoryItem) => boolean)[] = [],
  runtimeAndConditionList: ((
    record: TokenHistoryItem & { transactionAmount?: number; hasNft?: boolean },
  ) => boolean)[],
  filterTokenUuids?: string[],
) => {
  devLog('analysisHistoryList start', {
    orConditionFnList,
    andConditionList,
    runtimeAndConditionList,
  });

  const { history_list, project_dict, cex_dict, token_dict } = historyData;
  const filteredProjectValueMap: Record<string, number> = {}; // filtered project by token
  const projectValueMap: Record<string, number> = {}; // all project
  const filteredCexValueMap: Record<string, number> = {};
  const cexValueMap: Record<string, number> = {};
  const filteredAddrValueMap: Record<string, number> = {};
  const addrValueMap: Record<string, number> = {};
  const historyMap: Record<string, TokenHistoryItem> = {};
  const tokenValueMap: Record<string, number> = {};

  const filteredTokenValueMap: Record<string, number> = {}; // filtered token by category
  const displayHistoryList: string[] = [];

  let totalHistoryList: (TokenHistoryItem & { transactionAmount?: number; hasNft?: boolean })[] =
    history_list;

  if (andConditionList.length) {
    totalHistoryList = totalHistoryList.filter(record => andConditionList.every(fn => fn(record)));
  }

  totalHistoryList.forEach(record => {
    historyMap[record.id] = record;

    const isMeetFilterCondition =
      !orConditionFnList.length || orConditionFnList.some(fn => fn(record));

    const calcTokenValue = (
      tokenList: { token_id: string; amount: number; price?: number | undefined }[],
      onlyCalcValue = true,
    ) => {
      let total = 0;

      tokenList.forEach(token => {
        const tokenUuid = `${record.chain}_token:${token.token_id}`;
        const tokenInfo = token_dict[tokenUuid];
        const value = tokenInfo?.is_core ? Number((token.amount || 0) * (token.price || 0)) : 0;

        if (!onlyCalcValue) {
          addNewRecordValueToMap(tokenValueMap, tokenUuid, value);

          if (isMeetFilterCondition) {
            addNewRecordValueToMap(filteredTokenValueMap, tokenUuid, value);

            if (filterTokenUuids?.length && filterTokenUuids.includes(tokenUuid)) {
              displayHistoryList.push(record.id);
            }
          }
        }

        total += value;
        const isNft = token.token_id.length === 32;
        if (isNft) {
          record.hasNft = true;
        }
      });

      return total;
    };

    const sendValue = calcTokenValue(record.sends);
    const receiveValue = calcTokenValue(record.receives);
    const totalValue = Math.max(sendValue, receiveValue);
    record.transactionAmount = totalValue;

    if (runtimeAndConditionList.length && !runtimeAndConditionList.every(fn => fn(record))) {
      return;
    }

    if (isMeetFilterCondition && !filterTokenUuids?.length) {
      displayHistoryList.push(record.id);
    }
    calcTokenValue(record.sends, false);
    calcTokenValue(record.receives, false);

    if (record.project_id) {
      addNewRecordValueToMap(projectValueMap, record.project_id, totalValue);
    } else if (record.cex_id) {
      addNewRecordValueToMap(cexValueMap, record.cex_id, totalValue);
    } else if (record.other_addr) {
      addNewRecordValueToMap(addrValueMap, record.other_addr, totalValue);
    }
    if (filterTokenUuids?.length) {
      const hasTargetToken = record.sends.concat(record.receives).some(item => {
        const tokenUuid = `${record.chain}_token:${item.token_id}`;
        if (filterTokenUuids.includes(tokenUuid)) {
          return true;
        }
        return false;
      });

      if (!hasTargetToken) {
        return;
      }
    }
    if (record.project_id) {
      addNewRecordValueToMap(filteredProjectValueMap, record.project_id, totalValue);
    } else if (record.cex_id) {
      addNewRecordValueToMap(filteredCexValueMap, record.cex_id, totalValue);
    } else if (record.other_addr) {
      addNewRecordValueToMap(filteredAddrValueMap, record.other_addr, totalValue);
    }
  });

  const filteredProjectList = convertProjectValueMapToTopList(
    filteredProjectValueMap,
    project_dict,
  );
  const projectList = convertProjectValueMapToTopList(projectValueMap, project_dict);

  const filteredCexList = convertCexValueMapToTopList(filteredCexValueMap, cex_dict);
  const cexList = convertCexValueMapToTopList(cexValueMap, cex_dict);

  const filteredOtherAddrList = convertAddrValueMapToTopList(filteredAddrValueMap);
  const otherAddrList = convertAddrValueMapToTopList(addrValueMap);

  const filteredTopCategoryList: TopItem[] = [
    ...filteredProjectList,
    ...filteredCexList,
    ...filteredOtherAddrList,
  ].sort((a, b) => b.value - a.value);

  const topCategoryList: TopItem[] = [...projectList, ...cexList, ...otherAddrList]
    .sort((a, b) => b.value - a.value)
    .slice(0, TOP_CATEGORY_COUNT);

  const filteredTopTokenList: TopItem[] = Object.keys(filteredTokenValueMap)
    .map(tokenUuid => {
      const token = token_dict[tokenUuid];
      return {
        value: filteredTokenValueMap[tokenUuid],
        type: 'token' as const,
        chain: token?.chain,
        name: getTokenSymbol(token),
        icon: token?.logo_url,
        id: tokenUuid,
      };
    })
    .filter(item => {
      const token = token_dict[item.id];
      return token && token?.id.length !== 32 && token.is_core;
    }) // filter nft token
    .sort((a, b) => b.value - a.value);

  const topTokenList: TopItem[] = Object.keys(tokenValueMap)
    .map(tokenUuid => {
      const token = token_dict[tokenUuid];
      return {
        value: tokenValueMap[tokenUuid],
        type: 'token' as const,
        chain: token?.chain,
        name: getTokenSymbol(token),
        icon: token?.logo_url,
        id: tokenUuid,
      };
    })
    .filter(item => {
      const token = token_dict[item.id];
      return token && token?.id.length !== 32 && token.is_core;
    }) // filter nft token
    .sort((a, b) => b.value - a.value)
    .slice(0, TOP_TOKEN_COUNT);

  const filterHistoryList = Array.from(new Set(displayHistoryList))
    .map(id => historyMap[id])
    .sort((a, b) => b.time_at - a.time_at);

  const result = {
    // filteredProjectValueMap,
    // filteredCexValueMap,
    // filteredAddrValueMap,
    // historyMap,
    // tokenValueMap,
    topCategoryList, // top Category
    filteredTopCategoryList, // all filtered category
    topTokenList,
    filteredTopTokenList,
    filterHistoryList,
    token_dict,
    project_dict,
  };
  devLog('analysisHistoryList end', result);
  return result;
};

enum FieldEnum {
  DATE = 'Date',
  TIME = 'Time',
  CHAIN = 'Chain',
  TX_HASH = 'Tx hash',
  TX_LINK = 'Link',
  TX_TYPE = 'Tx type',
  RELATED_ADDR = 'Related addr',
  PROTOCOL = 'Protocol',
  ASSET = 'Asset',
  ASSET_TYPE = 'Asset type',
  ASSET_BALANCE_CHANGE = 'Asset balance change',
  ASSET_USD_VALUE_CHANGE = 'Asset USD value change',
  IS_SCAM = 'Is scam',
  USD_GAS_FEE = 'USD gas fee',
}

// the same implementation as on the python script from backend: https://github.com/DeBankDeFi/DeBankCore/blob/master/async_query/executors/transaction_export.py#L66
export const exportHistoryList = (data: {
  transactions?: TokenHistoryItem[];
  token_dict?:
    | {
        [index: string]: Tokens;
      }
    | undefined;
  protocol_dict?: Record<string, ProjectDetail>;
  address: string;
}) => {
  const { transactions = [], token_dict = {}, protocol_dict = {}, address } = data;
  const rows: any = [];

  transactions.forEach(t => {
    const tx_id = t['id'];
    const tx = t['tx'];
    const tx_type = t['cate_id'] || (tx && tx['name']) || 'Contract Interaction';

    if (tx && tx['status'] === 0) {
      return;
    }
    const chain_id = t['chain'];
    const link = getExplorerLink(chain_id, tx_id);
    const sends = t['sends'];
    const receives = t['receives'];
    const related_addr = t['other_addr'];
    const protocol_id = t['project_id'];
    const is_scam = t['is_scam'] ? 'yes' : 'no';
    const protocol_name = protocol_id ? protocol_dict[protocol_id]['name'] : '';
    const time_at = t['time_at'];
    const usd_gas_fee = tx && tx['usd_gas_fee'] ? formatNum(tx['usd_gas_fee'], 2) : '';

    sends.forEach(e => {
      e['amount'] *= -1;
    });

    if (receives.length || sends.length) {
      let is_first = true;

      receives.concat(sends).forEach(token => {
        const tid = `${chain_id}_token:${token.token_id}`;
        const token_type = tid.length === 32 ? 'NFT' : 'token';
        const amount = token['amount'];

        const token_info = token_dict[tid];

        if (!token_info) {
          return;
        }
        const token_name = token_info['symbol'];
        let usd_value = 0;
        if ('price' in token && typeof token['price'] === 'number') {
          usd_value = amount * (token['price'] || 0);
        } else {
          const usd_price = token_info['price'];
          usd_value = usd_price ? amount * usd_price : 0;
        }
        let row = {};
        if (is_first) {
          is_first = false;
          row = {
            [FieldEnum.DATE]: dayjs.unix(time_at).format('DD/MM/YYYY'),
            [FieldEnum.TIME]: dayjs.unix(time_at).format('HH:mm:ss'),
            [FieldEnum.CHAIN]: chain_id,
            [FieldEnum.TX_HASH]: tx_id,
            [FieldEnum.TX_LINK]: link,
            [FieldEnum.TX_TYPE]: tx_type,
            [FieldEnum.RELATED_ADDR]: related_addr,
            [FieldEnum.PROTOCOL]: protocol_name,
            [FieldEnum.IS_SCAM]: is_scam,
            [FieldEnum.USD_GAS_FEE]: usd_gas_fee,
          };
        } else {
          row = {};
        }
        row = {
          ...row,
          [FieldEnum.ASSET]: token_name,
          [FieldEnum.ASSET_TYPE]: token_type,
          [FieldEnum.ASSET_BALANCE_CHANGE]: amount,
          [FieldEnum.ASSET_USD_VALUE_CHANGE]: usd_value,
        };

        rows.push(row);
      });
    } else {
      const row = {
        [FieldEnum.DATE]: dayjs.unix(time_at).format('DD/MM/YYYY'),
        [FieldEnum.TIME]: dayjs.unix(time_at).format('HH:mm:ss'),
        [FieldEnum.CHAIN]: chain_id,
        [FieldEnum.TX_HASH]: tx_id,
        [FieldEnum.TX_LINK]: link,
        [FieldEnum.TX_TYPE]: tx_type,
        [FieldEnum.RELATED_ADDR]: related_addr,
        [FieldEnum.PROTOCOL]: protocol_name,
        [FieldEnum.IS_SCAM]: is_scam,
        [FieldEnum.USD_GAS_FEE]: usd_gas_fee,
      };
      rows.push(row);
    }
  });

  devLog('export csv', rows);

  jsonexport(
    rows,
    {
      headers: [
        FieldEnum.DATE,
        FieldEnum.TIME,
        FieldEnum.CHAIN,
        FieldEnum.TX_HASH,
        FieldEnum.TX_LINK,
        FieldEnum.TX_TYPE,
        FieldEnum.RELATED_ADDR,
        FieldEnum.PROTOCOL,
        FieldEnum.ASSET,
        FieldEnum.ASSET_TYPE,
        FieldEnum.ASSET_BALANCE_CHANGE,
        FieldEnum.ASSET_USD_VALUE_CHANGE,
        FieldEnum.IS_SCAM,
        FieldEnum.USD_GAS_FEE,
      ],
    },
    (err: Error, csv: string) => {
      if (err) {
        console.warn('export history error: ', err);
        message.error(err.message);
        return;
      }
      console.log(csv);
      downloadCSV(csv || '', `${address}_history`);
    },
  );
};
